import { useState, useEffect } from 'react';
import { TreeItem, BasketItem } from '../types';
import { fetchWithAuth } from '../lib/fetchWithAuth';

// Central basket state + checkout flow shared by GardenPage and AddTreePage.
// `onAfterPaymentSuccess` lets each page run its own post-purchase side effect
// (e.g. GardenPage refetches nearby marketplace listings).
export function useBasket(onAfterPaymentSuccess?: () => void) {
    const [basket, setBasket] = useState<BasketItem[]>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('mitifresh_basket');
            return saved ? JSON.parse(saved) : [];
        }
        return [];
    });
    const [isBasketOpen, setIsBasketOpen] = useState(false);
    const [flashBasket, setFlashBasket] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);

    useEffect(() => {
        localStorage.setItem('mitifresh_basket', JSON.stringify(basket));
    }, [basket]);

    const addToBasket = async (tree: TreeItem) => {
        const isAlreadyInBasket = basket.some(item => item.tree._id === tree._id);
        if (isAlreadyInBasket) {
            alert(`"${tree.name}" is already added to your Basket! You can modify its quantity inside the sidebar drawer.`);
            return;
        }
        setBasket((prevBasket) => [...prevBasket, { tree, quantity: 1 }]);
        setFlashBasket(true);
        setTimeout(() => setFlashBasket(false), 300);
    };

    const updateQuantity = async (treeId: string, increment: boolean) => {
        const existingItem = basket.find(item => item.tree._id === treeId);
        if (!existingItem) return;
        if (!increment && existingItem.quantity <= 1) return;

        const targetQuantity = increment ? existingItem.quantity + 1 : existingItem.quantity - 1;
        setBasket(prev => prev.map(item => item.tree._id === treeId ? { ...item, quantity: targetQuantity } : item));
    };

    const removeFromBasket = async (treeId: string) => {
        setBasket(prev => prev.filter(item => item.tree._id !== treeId));
    };

    const handleCheckout = async () => {
        if (basket.length === 0) return;
        setCheckoutLoading(true);
        try {
            const checkoutLineItems = basket.map(item => ({
                name: item.tree.name,
                price: item.tree.price,
                quantity: item.quantity
            }));

            const response = await fetchWithAuth('/api/checkout/create-payment-intent', {
                method: 'POST',
                body: JSON.stringify({ items: checkoutLineItems }),
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || "Failed initializing payment intent sequence.");
            }

            const { clientSecret: returnedSecret } = await response.json();
            setClientSecret(returnedSecret);
        } catch (err: any) {
            alert(`Handshake Error: ${err.message || err}`);
        } finally {
            setCheckoutLoading(false);
        }
    };

    const handlePaymentSuccess = async () => {
        try {
            const orderPayloadItems = basket.map(item => ({
                id: item.tree._id,
                name: item.tree.name,
                price: item.tree.price.toString(),
                quantity: item.quantity,
                userId: item.tree.user
            }));

            await fetchWithAuth('/api/orders', {
                method: 'POST',
                body: JSON.stringify({ items: orderPayloadItems })
            });
        } catch (error) {
            console.error("Order creation or sync failed:", error);
        }

        setShowPaymentSuccess(true);
        setBasket([]);
        setClientSecret(null);
        setIsBasketOpen(false);
        onAfterPaymentSuccess?.();
    };

    const totalItemsCount = basket.reduce((acc, item) => acc + item.quantity, 0);
    const grandTotalPrice = basket.reduce((acc, item) => acc + (item.tree.price * item.quantity), 0);

    return {
        basket, isBasketOpen, setIsBasketOpen, flashBasket, checkoutLoading,
        clientSecret, setClientSecret, showPaymentSuccess, setShowPaymentSuccess,
        addToBasket, updateQuantity, removeFromBasket, handleCheckout, handlePaymentSuccess,
        totalItemsCount, grandTotalPrice,
    };
}
